/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import { AppLanguage, ChildProfile } from '../types';
import { useLanguageActions } from '../context/LanguageContext';
import Mascot from './Mascot';
import CatAvatar from './CatAvatar';
import { playSynthSound, speakText } from '../utils/speaker';
import { User, Plus, Check, ChevronRight, Volume2, Globe } from 'lucide-react';

interface SplashAndProfileProps {
  currentLanguage: AppLanguage;
  setLanguage: (lang: AppLanguage) => void;
  profiles: ChildProfile[];
  onSelectProfile: (profile: ChildProfile) => void;
  onCreateProfile: (name: string, avatar: string, color: string) => void;
  soundEnabled: boolean;
}

const AVATARS = [
  { emoji: '🐱', label: 'Kitty' },
  { emoji: '🦕', label: 'Dino' },
  { emoji: '🐸', label: 'Froggy' },
  { emoji: '🐨', label: 'Koala' },
  { emoji: '🦁', label: 'Lion' },
  { emoji: '🧸', label: 'Teddy' },
  { emoji: '🐶', label: 'Puppy' },
  { emoji: '🚀', label: 'Rocket' }
];

const COLORS = [
  { bg: 'bg-coralpink border-rose-300', value: 'bg-coralpink', name: 'Coral' },
  { bg: 'bg-skyblue border-blue-300', value: 'bg-skyblue', name: 'Sky Blue' },
  { bg: 'bg-mintgreen border-emerald-300', value: 'bg-mintgreen', name: 'Mint Green' },
  { bg: 'bg-softyellow border-yellow-300', value: 'bg-softyellow', name: 'Yellow' },
  { bg: 'bg-lavender border-purple-300', value: 'bg-lavender', name: 'Lavender' },
  { bg: 'bg-orangeaccent border-orange-300', value: 'bg-orangeaccent', name: 'Orange' }
];

const TRANSLATIONS = {
  en: {
    title: 'Little Learning World',
    subtitle: 'Learn Alphabets, Words, & Numbers!',
    play: "Let's Play!",
    who: "Who is learning today?",
    addProfile: "Add New Kid",
    createTitle: "Create Your Profile",
    enterName: "Type your name...",
    chooseAvatar: "Choose your animal pet!",
    chooseColor: "Choose your favorite color!",
    createBtn: "Ready, Go!",
    greeting: "Let's learn!"
  },
  ur: {
    title: 'ننھی دنیا',
    subtitle: 'حروف، الفاظ اور گنتی سیکھیں!',
    play: "آئیں کھیلیں!",
    who: "آج کون سیکھ رہا ہے؟",
    addProfile: "نیا بچہ شامل کریں",
    createTitle: "اپنا پروفائل بنائیں",
    enterName: "اپنا نام لکھیں...",
    chooseAvatar: "اپنا پیارا جانور چنیں!",
    chooseColor: "اپنا پسندیدہ رنگ چنیں!",
    createBtn: "شروع کریں!",
    greeting: "آئیں سیکھیں!"
  },
  ar: {
    title: 'عالم التعلم الصغير',
    subtitle: 'تعلم الحروف، الكلمات، والأرقام!',
    play: "لنبدأ اللعب!",
    who: "من يتعلم اليوم؟",
    addProfile: "إضافة طفل جديد",
    createTitle: "أنشئ ملفك الشخصي",
    enterName: "اكتب اسمك هنا...",
    chooseAvatar: "اختر حيوانك الأليف!",
    chooseColor: "اختر لونك المفضل!",
    createBtn: "هيا بنا!",
    greeting: "لنبدأ التعلم!"
  }
};

export default function SplashAndProfile({
  currentLanguage,
  setLanguage,
  profiles,
  onSelectProfile,
  onCreateProfile,
  soundEnabled
}: SplashAndProfileProps) {
  const { loadLanguageData } = useLanguageActions();
  const [subScreen, setSubScreen] = useState<'language-select' | 'splash' | 'profile-select' | 'profile-create'>('language-select');
  const [newName, setNewName] = useState('');
  const [selectedAvatar, setSelectedAvatar] = useState('🐱');
  const [selectedColor, setSelectedColor] = useState('bg-skyblue');

  const t = TRANSLATIONS[currentLanguage] || TRANSLATIONS.en;
  const isRtl = currentLanguage === 'ur' || currentLanguage === 'ar';

  // Speak initial greeting on load
  useEffect(() => {
    if (subScreen === 'language-select') {
      speakText("Welcome! Please choose your language. خوش آمدید! اپنی زبان منتخب کریں۔ أهلاً بك! الرجاء اختيار لغتك.", currentLanguage, soundEnabled);
    } else if (subScreen === 'splash') {
      speakText(t.title + ". " + t.subtitle, currentLanguage, soundEnabled);
    }
  }, [currentLanguage, subScreen]);

  const handleLanguageChange = async (lang: AppLanguage) => {
    playSynthSound('click');
    try {
      await loadLanguageData(lang);
    } catch (e) {
      console.error(e);
    }
    setLanguage(lang);
  };

  const handleStartClick = () => {
    playSynthSound('click');
    if (profiles.length === 0) {
      setSubScreen('profile-create');
    } else {
      setSubScreen('profile-select');
    }
  };

  const handleCreateSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newName.trim()) return;
    playSynthSound('badge');
    onCreateProfile(newName.trim(), selectedAvatar, selectedColor);
    setNewName('');
  };

  return (
    <div id="splash-screen" className="min-h-screen bg-cream flex flex-col justify-between p-4 md:p-8 overflow-hidden relative">
      {/* Decorative floating shapes */}
      <div className="absolute top-[10%] left-[5%] w-16 h-16 bg-skyblue/10 rounded-full blur-xl animate-pulse" />
      <div className="absolute bottom-[20%] right-[5%] w-24 h-24 bg-coralpink/15 rounded-full blur-xl animate-pulse" />
      <div className="absolute top-[40%] right-[10%] w-20 h-20 bg-softyellow/10 rounded-full blur-lg" />

      {/* Language Quick Toggle Bar at top */}
      <div className="flex justify-between items-center z-10 w-full max-w-4xl mx-auto">
        <div className="flex gap-2 bg-white/75 backdrop-blur-sm p-1.5 rounded-full shadow-sm border border-slate-100">
          <button
            onClick={() => handleLanguageChange('en')}
            className={`px-4 py-1.5 rounded-full text-xs md:text-sm font-black transition-all ${
              currentLanguage === 'en'
                ? 'bg-skyblue text-navy shadow-sm'
                : 'text-navy hover:bg-slate-100'
            }`}
          >
            🇺🇸 English
          </button>
          <button
            onClick={() => handleLanguageChange('ur')}
            className={`px-4 py-1.5 rounded-full text-xs md:text-sm font-black font-urdu transition-all leading-none ${
              currentLanguage === 'ur'
                ? 'bg-orangeaccent text-navy shadow-sm font-bold'
                : 'text-navy hover:bg-slate-100'
            }`}
          >
            اردو
          </button>
          <button
            onClick={() => handleLanguageChange('ar')}
            className={`px-4 py-1.5 rounded-full text-xs md:text-sm font-black font-arabic transition-all ${
              currentLanguage === 'ar'
                ? 'bg-mintgreen text-navy shadow-sm font-bold'
                : 'text-navy hover:bg-slate-100'
            }`}
          >
            العربية
          </button>
        </div>

        {/* Brand visual mini indicator */}
        <div className="flex items-center gap-1 bg-white/75 px-3 py-1.5 rounded-full border border-slate-100 shadow-sm">
          <span className="text-xl">⭐</span>
          <span className="text-xs font-black text-navy tracking-tight">KIDS SAFE</span>
        </div>
      </div>

      {/* Main interactive area */}
      <div className="flex-grow flex flex-col justify-center items-center z-10 w-full max-w-4xl mx-auto my-6">
        {subScreen === 'language-select' && (
          <div className="flex flex-col items-center text-center gap-6 w-full max-w-2xl px-4">
            {/* Mascot */}
            <div className="kid-float mb-2">
              <Mascot mood="happy" lang={currentLanguage} />
            </div>

            {/* Header prompt */}
            <div className="space-y-2">
              <motion.h1
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="text-4xl md:text-5xl font-black text-navy tracking-tight leading-none"
              >
                <span className="bg-gradient-to-r from-coralpink via-orangeaccent to-skyblue bg-clip-text text-transparent">
                  Choose Your Language!
                </span>
              </motion.h1>
              <p className="text-xs md:text-sm font-black text-navy uppercase tracking-widest">
                اپنی زبان منتخب کریں • اختر لغتك
              </p>
            </div>

            {/* Three Big, Beautiful, Child-Friendly Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 w-full mt-4 justify-center">
              {/* English Card */}
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={async () => {
                  playSynthSound('star');
                  await handleLanguageChange('en');
                  if (profiles.length > 0) {
                    onSelectProfile(profiles[0]);
                  } else {
                    setSubScreen('profile-create');
                  }
                }}
                className="cursor-pointer rounded-3xl p-6 bg-white border-4 border-[#4FC3F7]/30 hover:border-[#4FC3F7] shadow-[0_8px_0_#4FC3F7]/20 flex flex-col items-center justify-center gap-4 transition-all min-h-[160px]"
              >
                <span className="text-6xl">🇺🇸</span>
                <span className="font-black text-2xl text-slate-800">English</span>
                <span className="text-[11px] uppercase font-black text-navy tracking-wider">Learn A B C</span>
              </motion.div>

              {/* Urdu Card */}
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={async () => {
                  playSynthSound('star');
                  await handleLanguageChange('ur');
                  if (profiles.length > 0) {
                    onSelectProfile(profiles[0]);
                  } else {
                    setSubScreen('profile-create');
                  }
                }}
                className="cursor-pointer rounded-3xl p-6 bg-white border-4 border-[#FFA726]/30 hover:border-[#FFA726] shadow-[0_8px_0_#FFA726]/20 flex flex-col items-center justify-center gap-4 transition-all min-h-[160px]"
              >
                <span className="text-6xl font-urdu">🇵🇰</span>
                <span className="font-black text-2xl font-urdu text-slate-800 leading-none">اردو</span>
                <span className="text-[11px] uppercase font-black text-navy tracking-wider">حروف تہجی سیکھیں</span>
              </motion.div>

              {/* Arabic Card */}
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={async () => {
                  playSynthSound('star');
                  await handleLanguageChange('ar');
                  if (profiles.length > 0) {
                    onSelectProfile(profiles[0]);
                  } else {
                    setSubScreen('profile-create');
                  }
                }}
                className="cursor-pointer rounded-3xl p-6 bg-white border-4 border-[#81C784]/30 hover:border-[#81C784] shadow-[0_8px_0_#81C784]/20 flex flex-col items-center justify-center gap-4 transition-all min-h-[160px]"
              >
                <span className="text-6xl">🇸🇦</span>
                <span className="font-black text-2xl font-arabic text-slate-800 leading-none">العربية</span>
                <span className="text-[11px] uppercase font-black text-navy tracking-wider">تعلم الحروف</span>
              </motion.div>
            </div>
          </div>
        )}

        {subScreen === 'splash' && (
          <div className="flex flex-col items-center text-center gap-8">
            {/* Mascot */}
            <div className="kid-float">
              <Mascot mood="idle" lang={currentLanguage} />
            </div>

            {/* Title Container */}
            <div className="space-y-3">
              <motion.h1
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className={`text-5xl md:text-7xl font-black text-navy leading-tight ${
                  currentLanguage === 'ur' ? 'font-urdu' : currentLanguage === 'ar' ? 'font-arabic' : ''
                }`}
              >
                <span className="bg-gradient-to-r from-coralpink via-orangeaccent to-skyblue bg-clip-text text-transparent">
                  {t.title}
                </span>
              </motion.h1>
              <p className="text-lg md:text-2xl font-black text-navy max-w-md mx-auto">
                {t.subtitle}
              </p>
            </div>

            {/* Big Child-Safe Play Button (Target size > 56px) */}
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={handleStartClick}
              className="px-14 py-6 bg-rose-400 hover:bg-rose-500 text-white font-black text-3xl rounded-3xl border-b-8 border-rose-600 shadow-lg cursor-pointer flex items-center gap-3 transition-colors duration-150"
            >
              <span>{t.play}</span>
              <ChevronRight className="w-8 h-8" />
            </motion.button>
          </div>
        )}

        {subScreen === 'profile-select' && (
          <div className="w-full flex flex-col items-center gap-6">
            <Mascot mood="happy" lang={currentLanguage} />
            
            <h2 className={`text-3xl md:text-4xl font-black text-navy mb-2 ${
              isRtl ? 'text-right' : ''
            }`}>
              {t.who}
            </h2>

            {/* Grid of profiles */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 w-full px-4 max-w-2xl justify-center">
              {profiles.map((profile) => (
                <motion.div
                  key={profile.id}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    playSynthSound('star');
                    onSelectProfile(profile);
                  }}
                  className={`cursor-pointer rounded-3xl p-5 border-4 border-slate-700/10 flex flex-col items-center justify-between gap-3 child-shadow transition-transform ${profile.bgColor}`}
                >
                  {profile.photo ? (
                    <img
                      src={profile.photo}
                      alt={profile.name}
                      referrerPolicy="no-referrer"
                      loading="lazy"
                      className="w-16 h-16 rounded-full object-cover border-2 border-white shadow-md"
                    />
                  ) : profile.avatar === '🐱' ? (
                    <div className="w-16 h-16 flex items-center justify-center p-1">
                      <CatAvatar
                        expression={profile.avatarCustom?.expression || 'happy'}
                        color={profile.avatarCustom?.color || 'orange'}
                        accessory={profile.avatarCustom?.accessory || 'none'}
                        size={64}
                        className="filter drop-shadow-md"
                      />
                    </div>
                  ) : (
                    <span className="text-6xl filter drop-shadow-sm">{profile.avatar}</span>
                  )}
                  <div className="text-center w-full">
                    <p className="font-black text-xl text-navy truncate px-1">{profile.name}</p>
                    <div className="flex items-center justify-center gap-1 mt-1 bg-white/40 px-2.5 py-0.5 rounded-full w-fit mx-auto">
                      <span className="text-sm">⭐</span>
                      <span className="text-xs font-black text-slate-800">{profile.stars}</span>
                    </div>
                  </div>
                </motion.div>
              ))}

              {/* Add profile card */}
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  playSynthSound('click');
                  setSubScreen('profile-create');
                }}
                className="cursor-pointer bg-white rounded-3xl p-5 border-4 border-dashed border-slate-300 flex flex-col items-center justify-center gap-4 child-shadow text-navy hover:border-slate-800 hover:text-slate-800 min-h-[160px]"
              >
                <div className="w-14 h-14 rounded-full bg-slate-50 flex items-center justify-center border-3 border-dashed border-slate-200">
                  <Plus className="w-8 h-8" />
                </div>
                <p className="font-black text-sm md:text-base text-center text-navy">{t.addProfile}</p>
              </motion.div>
            </div>

            {/* Back to main */}
            <button
              onClick={() => { playSynthSound('click'); setSubScreen('language-select'); }}
              className="mt-6 text-navy font-black hover:text-black"
            >
              ← Back
            </button>
          </div>
        )}

        {subScreen === 'profile-create' && (
          <div className="w-full max-w-xl bg-white rounded-3xl p-6 md:p-8 border-4 border-slate-100 child-shadow flex flex-col gap-6">
            <div className="flex items-center gap-3">
              <span className="text-3xl">🎨</span>
              <h2 className="text-2xl md:text-3xl font-black text-navy">{t.createTitle}</h2>
            </div>

            <form onSubmit={handleCreateSubmit} className="flex flex-col gap-6">
              {/* Name input */}
              <div className="space-y-2">
                <label className="text-sm font-black text-navy uppercase tracking-wider block">
                  {t.enterName}
                </label>
                <input
                  type="text"
                  required
                  maxLength={12}
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="e.g. Leo"
                  className="w-full px-5 py-4 text-xl font-bold bg-cream rounded-2xl border-3 border-amber-200 focus:border-amber-400 outline-none focus:ring-0 text-navy"
                />
              </div>

              {/* Avatar Selector */}
              <div className="space-y-2">
                <label className="text-sm font-black text-navy uppercase tracking-wider block">
                  {t.chooseAvatar}
                </label>
                <div className="grid grid-cols-4 gap-3 bg-cream/40 p-3 rounded-2xl border border-slate-100">
                  {AVATARS.map((av) => (
                    <button
                      key={av.emoji}
                      type="button"
                      onClick={() => {
                        playSynthSound('click');
                        setSelectedAvatar(av.emoji);
                        speakText(av.label, currentLanguage, soundEnabled);
                      }}
                      className={`py-3 text-4xl rounded-2xl border-3 transition-all ${
                        selectedAvatar === av.emoji
                          ? 'bg-amber-100 border-amber-400 scale-110 shadow-sm'
                          : 'bg-white border-transparent hover:border-slate-200'
                      }`}
                    >
                      {av.emoji}
                    </button>
                  ))}
                </div>
              </div>

              {/* Color Selector */}
              <div className="space-y-2">
                <label className="text-sm font-black text-navy uppercase tracking-wider block">
                  {t.chooseColor}
                </label>
                <div className="grid grid-cols-6 gap-3 bg-cream/40 p-3 rounded-2xl border border-slate-100">
                  {COLORS.map((col) => (
                    <button
                      key={col.value}
                      type="button"
                      onClick={() => {
                        playSynthSound('click');
                        setSelectedColor(col.value);
                      }}
                      className={`h-10 rounded-xl border-4 transition-all ${col.bg} ${
                        selectedColor === col.value
                          ? 'scale-110 border-slate-700'
                          : 'border-transparent'
                      }`}
                    />
                  ))}
                </div>
              </div>

              {/* Submit / Action button */}
              <div className="flex gap-4 items-center mt-4">
                <button
                  type="button"
                  onClick={() => {
                    playSynthSound('click');
                    if (profiles.length > 0) {
                      setSubScreen('profile-select');
                    } else {
                      setSubScreen('language-select');
                    }
                  }}
                  className="px-6 py-4 bg-slate-100 hover:bg-slate-200 text-navy font-black text-lg rounded-2xl cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-grow py-4 px-6 bg-emerald-400 hover:bg-emerald-500 text-white font-black text-xl rounded-2xl border-b-6 border-emerald-600 shadow-md flex items-center justify-center gap-2 cursor-pointer transition-colors"
                >
                  <span>{t.createBtn}</span>
                  <Check className="w-6 h-6" />
                </button>
              </div>
            </form>
          </div>
        )}
      </div>

      {/* Safety message at the very bottom */}
      <div className="text-center text-xs font-black text-navy max-w-md mx-auto z-10 py-2">
        🏠 100% Kid Safe • No Ads • Offline Learning • Parents Zone Password Protected
      </div>
    </div>
  );
}
